import React, { useState } from 'react';
import { Send, Loader2, CheckCircle2, MessageCircle, ShieldCheck, Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '919205514887';

const SERVICES = [
  'Stock / Inventory Audit',
  'Internal Audit',
  'Fixed Asset Verification',
  'SOP & Process Audit',
  'Warehouse Audit',
  'Risk & Compliance Review',
  'Manpower / Vendor Support',
  'Other'
];

const EMPTY = { name: '', mobile: '', email: '', company: '', city: '', service: SERVICES[0], message: '' };

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-[#0f2038] outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#1d4ed8] focus:ring-4 focus:ring-[#1d4ed8]/10';

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const buildWhatsAppText = (data) =>
    encodeURIComponent(
      `*New Audit Enquiry — AudiX Solutions & Co.*\n\n` +
        `Name: ${data.name}\n` +
        `Mobile: ${data.mobile}\n` +
        (data.email ? `Email: ${data.email}\n` : '') +
        (data.company ? `Company: ${data.company}\n` : '') +
        (data.city ? `City: ${data.city}\n` : '') +
        `Service: ${data.service}\n` +
        (data.message ? `\nRequirement: ${data.message}` : '')
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error('Please enter your name');
    if (form.mobile.replace(/\D/g, '').length < 10) return toast.error('Please enter a valid 10-digit mobile number');

    setSubmitting(true);
    const data = { ...form };
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === 'string' ? err.detail : 'Please check the details and try again';
        throw new Error(detail);
      }
      setSent(true);
      setForm(EMPTY);
      toast.success('Enquiry received. Opening WhatsApp so our team can respond faster.');
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppText(data)}`, '_blank');
    } catch (err) {
      toast.error(typeof err.message === 'string' ? err.message : 'Something went wrong. Please call us on 9205514887.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="scroll-mt-24 relative overflow-hidden bg-[#0f2038] py-20 md:py-28" data-testid="enquiry-section">
      <div className="ax-dot-bg pointer-events-none absolute inset-0 opacity-[0.07]" />
      <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#d8b45f]">Free Quote</p>
            <h2 className="ax-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08] text-white">
              Get a Free Audit Quote
            </h2>
            <div className="ax-rule mt-6" />
            <p className="mt-6 text-base md:text-lg leading-relaxed text-slate-300">
              Share your requirement and our team will revert with scope, timelines and a commercial estimate. No obligation, complete confidentiality.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { icon: Clock, t: 'Response within 24 working hours' },
                { icon: ShieldCheck, t: 'NDA-backed confidentiality on all enquiries' },
                { icon: Phone, t: 'Prefer talking? Call 9205514887 directly' }
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`enquiry-reassurance-${i}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10">
                    <r.icon className="h-4 w-4 text-[#d8b45f]" />
                  </span>
                  <span className="text-sm text-slate-300">{r.t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_40px_80px_-50px_rgba(0,0,0,0.6)] md:p-9">
              {sent ? (
                <div className="flex flex-col items-start gap-4 py-6" data-testid="enquiry-success">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
                    <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                  </span>
                  <h3 className="ax-display text-2xl font-extrabold text-[#0f2038]">Thank you — enquiry received</h3>
                  <p className="text-base text-slate-600">
                    Our team will get back to you shortly. If WhatsApp did not open automatically, tap below to send us the details directly.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}`, '_blank')}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-emerald-700 active:scale-[0.98]"
                      data-testid="enquiry-open-whatsapp-button"
                    >
                      <MessageCircle className="h-4 w-4" /> Open WhatsApp
                    </button>
                    <button
                      onClick={() => setSent(false)}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3 text-sm font-bold text-[#0f2038] transition-all hover:bg-slate-50"
                      data-testid="enquiry-new-button"
                    >
                      Submit another enquiry
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="enquiry-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Name*</label>
                      <input className={inputClass} value={form.name} onChange={set('name')} placeholder="Your full name" data-testid="enquiry-name-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Mobile*</label>
                      <input className={inputClass} value={form.mobile} onChange={set('mobile')} placeholder="10-digit mobile number" inputMode="tel" data-testid="enquiry-mobile-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Email</label>
                      <input className={inputClass} value={form.email} onChange={set('email')} placeholder="name@company.com" data-testid="enquiry-email-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Company</label>
                      <input className={inputClass} value={form.company} onChange={set('company')} placeholder="Company name" data-testid="enquiry-company-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">City</label>
                      <input className={inputClass} value={form.city} onChange={set('city')} placeholder="Delhi / Gurugram / Noida" data-testid="enquiry-city-input" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Service required*</label>
                      <select className={inputClass} value={form.service} onChange={set('service')} data-testid="enquiry-service-select">
                        {SERVICES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Requirement details</label>
                    <textarea
                      className={`${inputClass} min-h-[110px] resize-y`}
                      value={form.message}
                      onChange={set('message')}
                      placeholder="Number of locations, stock value, timelines, any specific scope..."
                      data-testid="enquiry-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0f2038] px-8 py-4 text-base font-bold text-white shadow-[0_16px_36px_-18px_rgba(15,32,56,0.9)] transition-all duration-200 hover:bg-[#16304f] active:scale-[0.99] disabled:opacity-60 sm:w-auto"
                    data-testid="enquiry-submit-button"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? 'Sending...' : 'Send Enquiry & Open WhatsApp'}
                  </button>
                  <p className="text-xs text-slate-500">
                    Submitting saves your enquiry with us and opens WhatsApp with the details pre-filled so our team can respond immediately.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
